import Keycloak from 'keycloak-js'

// url must be the bare Keycloak host (NO /auth suffix): the backend validates
// issuer `${SSO_HOST}/realms/${SSO_REALM}` (see backend/src/middlewares/authMiddleware.js).
const keycloak = new Keycloak({
  url: process.env.REACT_APP_SSO_HOST || 'https://sso.dokiapp.hu',
  realm: process.env.REACT_APP_SSO_REALM || 'dokiapp-local',
  clientId: process.env.REACT_APP_SSO_CLIENT_ID || 'doki-web',
})

// The app stores one token in localStorage.token, which can come from two
// issuers: the legacy password flow (HS256, /api/platform/auth/login) or
// Keycloak (RS256). tokenSource records which one, so token sync, request
// retry, and logout can tell an SSO session from a legacy one without
// decoding the JWT. Keycloak may hold a cookie for a DIFFERENT account than
// the legacy session, so a live legacy token must never be replaced by an
// SSO token — this flag is what makes that guard possible.
const TOKEN_SOURCE_KEY = 'tokenSource'

export const isSsoSession = () =>
  localStorage.getItem(TOKEN_SOURCE_KEY) === 'sso'

export const markSsoSession = () =>
  localStorage.setItem(TOKEN_SOURCE_KEY, 'sso')

export const clearSsoSession = () => localStorage.removeItem(TOKEN_SOURCE_KEY)

// The app's own language codes are not all valid Keycloak locales: Ukrainian
// is 'ua' here but 'uk' (ISO 639-1) in Keycloak. Anything not in this map is
// sent without a locale, so Keycloak falls back to the realm default instead
// of receiving a code it cannot resolve.
const KC_LOCALE_BY_APP_LANGUAGE = {
  hu: 'hu',
  en: 'en',
  ua: 'uk',
  uk: 'uk',
}

// i18n.language is normally one of 'hu' | 'en' | 'ua', but a region suffix
// ('hu-HU', 'en-GB') is still possible, so only the base subtag is looked up.
const toKcLocale = (language) =>
  KC_LOCALE_BY_APP_LANGUAGE[
    String(language || '')
      .toLowerCase()
      .split('-')[0]
  ]

// keycloak.login() can only emit `ui_locales` (via its `locale` option), but
// Keycloak's own `kc_locale` takes priority over it AND persists the choice in
// the KEYCLOAK_LOCALE cookie for the rest of the login flow. So the URL is
// built and then extended by hand; the navigation is exactly what the default
// adapter's login() does internally:
//   window.location.assign(await keycloak.createLoginUrl(options))
// createLoginUrl also stores state/nonce/PKCE for the callback, so navigating
// to the returned URL ourselves keeps the flow intact.
export const loginWithLocale = async (language, options = {}) => {
  const locale = toKcLocale(language)
  const url = await keycloak.createLoginUrl(
    locale ? { ...options, locale } : options
  )

  window.location.assign(
    locale
      ? `${url}${url.includes('?') ? '&' : '?'}kc_locale=${encodeURIComponent(
          locale
        )}`
      : url
  )
}

export default keycloak
